import React, { useState, useEffect } from 'react';
import { 
  User, Mail, Phone, MapPin, Calendar, Award, Upload, FileText, 
  Edit, Save, X, Plus, Trash2, Camera, Building2, Clock, Star,
  Download, Eye, Shield, AlertCircle, CheckCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { toast } from '../hooks/use-toast';
import { apiService } from '../services/apiService';

const EmployeeProfile = ({ employeeId, onClose, canEdit = false }) => {
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  const [schools, setSchools] = useState([]);

  // Document management
  const [documents, setDocuments] = useState([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [showDocUpload, setShowDocUpload] = useState(false);

  // Employment history
  const [employmentHistory, setEmploymentHistory] = useState([]);
  const [showHistoryForm, setShowHistoryForm] = useState(false);

  // Emergency contacts
  const [emergencyContacts, setEmergencyContacts] = useState([]);
  const [showContactForm, setShowContactForm] = useState(false);

  useEffect(() => {
    if (employeeId) {
      loadEmployeeData();
    }
  }, [employeeId]);

  useEffect(() => {
    loadSchools();
  }, []);

  const getSchoolKey = (school) => String(school?.key || school?.school_number || school?.id || '');

  const loadSchools = async () => {
    try {
      const list = await apiService.getSchools();
      setSchools(list);
    } catch (error) {
      console.error('Failed to load schools:', error);
    }
  };

  const loadEmployeeData = async () => {
    try {
      setLoading(true);
      const data = await apiService.makeRequest(`/api/employees/${employeeId}?include_related=true`);
      setEmployee(data);
      setEditData(data);
      setDocuments(data.documents || []);
      setEmploymentHistory(data.employment_history || []);
      setEmergencyContacts(data.emergency_contacts || []);
    } catch (error) {
      console.error('Failed to load employee:', error);
      toast({
        title: 'Error',
        description: 'Failed to load employee data.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const updated = await apiService.updateEmployee(employeeId, editData);
      setEmployee(updated);
      setEditing(false);
      toast({
        title: 'Success',
        description: 'Employee profile updated successfully.'
      });
    } catch (error) {
      console.error('Failed to update employee:', error);
      toast({
        title: 'Error',
        description: 'Failed to update employee profile.',
        variant: 'destructive'
      });
    }
  };

  const handlePhotoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('photo', file);

      const response = await apiService.makeRequest(`/api/employees/${employeeId}/photo`, {
        method: 'POST',
        body: formData,
        headers: {} // Let browser set content-type for FormData
      });

      setEmployee(prev => ({ ...prev, photo_url: response.photo_url }));
      toast({
        title: 'Success',
        description: 'Profile photo updated successfully.'
      });
    } catch (error) {
      console.error('Failed to upload photo:', error);
      toast({
        title: 'Error',
        description: 'Failed to upload profile photo.',
        variant: 'destructive'
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDocumentUpload = async (formData) => {
    try {
      setUploadingDoc(true);
      const document = await apiService.makeRequest(`/api/employees/${employeeId}/documents`, {
        method: 'POST',
        body: formData,
        headers: {} // Let browser set content-type for FormData
      });
      setDocuments(prev => [document, ...prev]);
      setShowDocUpload(false);
      toast({
        title: 'Success',
        description: 'Document uploaded successfully.'
      });
    } catch (error) {
      console.error('Failed to upload document:', error);
      toast({
        title: 'Error',
        description: 'Failed to upload document.',
        variant: 'destructive'
      });
    } finally {
      setUploadingDoc(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="text-center py-8">
        <User className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Employee not found</h3>
        <p className="text-gray-600">The requested employee could not be found.</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <Card className="bg-gradient-to-r from-gray-900 to-black text-white">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6">
              <div className="relative">
                <Avatar className="w-24 h-24 border-4 border-yellow-500">
                  <AvatarImage src={employee.photo_url} alt={employee.display_name} />
                  <AvatarFallback className="bg-gray-700 text-white text-2xl">
                    {employee.first_name?.[0]}{employee.last_name?.[0]}
                  </AvatarFallback>
                </Avatar>
                {canEdit && (
                  <label className="absolute bottom-0 right-0 w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center cursor-pointer hover:bg-yellow-600 transition-colors">
                    <Camera className="w-4 h-4 text-black" />
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoUpload}
                      className="hidden"
                      disabled={uploading}
                    />
                  </label>
                )}
                {uploading && (
                  <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-yellow-500"></div>
                  </div>
                )}
              </div>
              
              <div className="flex-1">
                <h1 className="text-3xl font-bold mb-2">{employee.display_name}</h1>
                <p className="text-yellow-400 text-lg mb-2">{employee.position_title || 'Staff Member'}</p>
                <div className="flex items-center space-x-4 text-sm text-gray-300">
                  <div className="flex items-center">
                    <Building2 className="w-4 h-4 mr-1" />
                    {employee.school_name || 'Unassigned'}
                  </div>
                  <div className="flex items-center">
                    <Calendar className="w-4 h-4 mr-1" />
                    Hired {employee.hire_date ? new Date(employee.hire_date).toLocaleDateString() : 'Unknown'}
                  </div>
                  <Badge className={`${
                    employee.status === 'active' ? 'bg-green-500' :
                    employee.status === 'inactive' ? 'bg-yellow-500' :
                    'bg-red-500'
                  } text-white`}>
                    {employee.status?.toUpperCase() || 'UNKNOWN'}
                  </Badge>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              {canEdit && (
                <Button
                  onClick={() => editing ? handleSave() : setEditing(true)}
                  className="bg-yellow-500 hover:bg-yellow-600 text-black"
                >
                  {editing ? <Save className="w-4 h-4 mr-2" /> : <Edit className="w-4 h-4 mr-2" />}
                  {editing ? 'Save' : 'Edit'}
                </Button>
              )}
              {editing && (
                <Button
                  onClick={() => {
                    setEditing(false);
                    setEditData(employee);
                  }}
                  variant="outline"
                  className="border-gray-600 text-gray-300 hover:text-white"
                >
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
              )}
              <Button
                onClick={onClose}
                variant="outline"
                className="border-gray-600 text-gray-300 hover:text-white"
              >
                Close
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="profile">Profile</TabsTrigger>
          <TabsTrigger value="employment">Employment</TabsTrigger>
          <TabsTrigger value="documents">Documents</TabsTrigger>
          <TabsTrigger value="contacts">Contacts</TabsTrigger>
          <TabsTrigger value="timeline">Timeline</TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="space-y-6">
          <ProfileTab 
            employee={employee} 
            editData={editData} 
            setEditData={setEditData} 
            editing={editing}
            schools={schools}
            getSchoolKey={getSchoolKey}
          />
        </TabsContent>

        <TabsContent value="employment" className="space-y-6">
          <EmploymentTab 
            employee={employee}
            employmentHistory={employmentHistory}
            setEmploymentHistory={setEmploymentHistory}
            canEdit={canEdit}
          />
        </TabsContent>

        <TabsContent value="documents" className="space-y-6">
          <DocumentsTab 
            documents={documents}
            setDocuments={setDocuments}
            employeeId={employeeId}
            canEdit={canEdit}
          />
        </TabsContent>

        <TabsContent value="contacts" className="space-y-6">
          <ContactsTab 
            contacts={emergencyContacts}
            setContacts={setEmergencyContacts}
            employeeId={employeeId}
            canEdit={canEdit}
          />
        </TabsContent>

        <TabsContent value="timeline" className="space-y-6">
          <TimelineTab employeeId={employeeId} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

// Profile Tab Component
const ProfileTab = ({ employee, editData, setEditData, editing, schools = [], getSchoolKey }) => {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card>
        <CardHeader>
          <CardTitle>Basic Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>First Name</Label>
              {editing ? (
                <Input
                  value={editData.first_name || ''}
                  onChange={(e) => setEditData(prev => ({ ...prev, first_name: e.target.value }))}
                />
              ) : (
                <p className="text-gray-900">{employee.first_name}</p>
              )}
            </div>
            <div>
              <Label>Last Name</Label>
              {editing ? (
                <Input
                  value={editData.last_name || ''}
                  onChange={(e) => setEditData(prev => ({ ...prev, last_name: e.target.value }))}
                />
              ) : (
                <p className="text-gray-900">{employee.last_name}</p>
              )}
            </div>
          </div>
          
          <div>
            <Label>Email</Label>
            {editing ? (
              <Input
                type="email"
                value={editData.email || ''}
                onChange={(e) => setEditData(prev => ({ ...prev, email: e.target.value }))}
              />
            ) : (
              <div className="flex items-center">
                <Mail className="w-4 h-4 mr-2 text-gray-500" />
                <p className="text-gray-900">{employee.email}</p>
              </div>
            )}
          </div>

          <div>
            <Label>Phone</Label>
            {editing ? (
              <Input
                value={editData.phone_primary || ''}
                onChange={(e) => setEditData(prev => ({ ...prev, phone_primary: e.target.value }))}
              />
            ) : (
              <div className="flex items-center">
                <Phone className="w-4 h-4 mr-2 text-gray-500" />
                <p className="text-gray-900">{employee.phone_primary || 'Not provided'}</p>
              </div>
            )}
          </div>

          <div>
            <Label>Bio</Label>
            {editing ? (
              <Textarea
                value={editData.bio || ''}
                onChange={(e) => setEditData(prev => ({ ...prev, bio: e.target.value }))}
                placeholder="Brief bio or description..."
                rows={3}
              />
            ) : (
              <p className="text-gray-700">{employee.bio || 'No bio provided'}</p>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Professional Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Position Title</Label>
            {editing ? (
              <Input
                value={editData.position_title || ''}
                onChange={(e) => setEditData(prev => ({ ...prev, position_title: e.target.value }))}
              />
            ) : (
              <p className="text-gray-900">{employee.position_title || 'Not specified'}</p>
            )}
          </div>

          <div>
            <Label>Department</Label>
            {editing ? (
              <Select
                value={editData.department || ''}
                onValueChange={(value) => setEditData(prev => ({ ...prev, department: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select department" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Teaching Staff">Teaching Staff</SelectItem>
                  <SelectItem value="Administration">Administration</SelectItem>
                  <SelectItem value="Support Staff">Support Staff</SelectItem>
                  <SelectItem value="Maintenance">Maintenance</SelectItem>
                  <SelectItem value="Food Service">Food Service</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <p className="text-gray-900">{employee.department || 'Not specified'}</p>
            )}
          </div>

          <div>
            <Label>School</Label>
            {editing ? (
              <Select
                value={editData.school_id || ''}
                onValueChange={(value) => setEditData(prev => ({ ...prev, school_id: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select school" />
                </SelectTrigger>
                <SelectContent>
                  {schools.map((school) => (
                    <SelectItem key={school.id} value={getSchoolKey(school)}>
                      {school.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <p className="text-gray-900">{employee.school_name || 'Not specified'}</p>
            )}
          </div>

          <div>
            <Label>FTE</Label>
            {editing ? (
              <Input
                type="number"
                step="0.1"
                min="0"
                max="1"
                value={editData.fte || ''}
                onChange={(e) => {
                  let value = parseFloat(e.target.value);
                  if (isNaN(value)) value = 0;
                  value = Math.max(0, Math.min(1, value));
                  setEditData(prev => ({ ...prev, fte: value }));
                }}
              />
            ) : (
              <p className="text-gray-900">{employee.fte || 'Not specified'}</p>
            )}
          </div>

          <div>
            <Label>Skills</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {(employee.skills || []).map((skill, index) => (
                <Badge key={index} variant="secondary">{skill}</Badge>
              ))}
              {(!employee.skills || employee.skills.length === 0) && (
                <p className="text-gray-500 text-sm">No skills listed</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Employment Tab Component
const EmploymentTab = ({ employee, employmentHistory, setEmploymentHistory, canEdit }) => {
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Employment History</CardTitle>
          {canEdit && (
            <Button 
              className="bg-yellow-500 hover:bg-yellow-600 text-black"
              onClick={() => {
                // TODO: Implement add entry functionality
                toast({
                  title: 'Feature Coming Soon',
                  description: 'Employment history entry creation will be available soon.',
                });
              }}
              aria-label="Add employment history entry"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Entry
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {employmentHistory.length > 0 ? (
            <div className="space-y-4">
              {employmentHistory.map((entry, index) => (
                <div key={entry.id} className="border-l-4 border-yellow-500 pl-4 pb-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-gray-900">{entry.position_title}</h4>
                    <Badge variant={entry.end_date ? "secondary" : "default"}>
                      {entry.end_date ? "Past" : "Current"}
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-600">{entry.department}</p>
                  <p className="text-sm text-gray-500">
                    {new Date(entry.start_date).toLocaleDateString()} - 
                    {entry.end_date ? new Date(entry.end_date).toLocaleDateString() : 'Present'}
                  </p>
                  {entry.notes && (
                    <p className="text-sm text-gray-700 mt-2">{entry.notes}</p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Clock className="w-8 h-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-500">No employment history recorded</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Documents Tab Component
const DocumentsTab = ({ documents, setDocuments, employeeId, canEdit }) => {
  const [showUpload, setShowUpload] = useState(false);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Documents</CardTitle>
          {canEdit && (
            <Button 
              onClick={() => setShowUpload(true)}
              className="bg-yellow-500 hover:bg-yellow-600 text-black"
            >
              <Upload className="w-4 h-4 mr-2" />
              Upload Document
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {documents.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {documents.map((doc) => (
                <Card key={doc.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <FileText className="w-8 h-8 text-gray-600" />
                      {doc.is_confidential && (
                        <Shield className="w-4 h-4 text-red-500" />
                      )}
                    </div>
                    <h4 className="font-medium text-sm truncate">{doc.original_filename}</h4>
                    <p className="text-xs text-gray-500 capitalize">{doc.document_type.replace('_', ' ')}</p>
                    <p className="text-xs text-gray-400">
                      {new Date(doc.upload_date).toLocaleDateString()}
                    </p>
                    {doc.description && (
                      <p className="text-xs text-gray-600 mt-2 line-clamp-2">{doc.description}</p>
                    )}
                    <div className="flex justify-between mt-3">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => {
                          // Construct download URL and trigger download
                          const downloadUrl = `/api/employees/${employeeId}/documents/${doc.id}/download`;
                          window.open(downloadUrl, '_blank');
                        }}
                      >
                        <Download className="w-3 h-3 mr-1" />
                        Download
                      </Button>
                      {canEdit && (
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="text-red-600 hover:text-red-700"
                          onClick={async () => {
                            if (window.confirm('Are you sure you want to delete this document?')) {
                              try {
                                await apiService.makeRequest(`/api/employees/${employeeId}/documents/${doc.id}`, {
                                  method: 'DELETE'
                                });
                                // Optimistically remove from local state
                                setDocuments(prev => prev.filter(d => d.id !== doc.id));
                                toast({
                                  title: 'Success',
                                  description: 'Document deleted successfully'
                                });
                              } catch (error) {
                                console.error('Failed to delete document:', error);
                                toast({
                                  title: 'Error',
                                  description: 'Failed to delete document',
                                  variant: 'destructive'
                                });
                              }
                            }
                          }}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <FileText className="w-8 h-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-500">No documents uploaded</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upload Dialog */}
      <DocumentUploadDialog 
        open={showUpload}
        onClose={() => setShowUpload(false)}
        employeeId={employeeId}
        onUpload={(doc) => setDocuments(prev => [doc, ...prev])}
      />
    </div>
  );
};

// Contacts Tab Component
const ContactsTab = ({ contacts, setContacts, employeeId, canEdit }) => {
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Emergency Contacts</CardTitle>
          {canEdit && (
            <Button className="bg-yellow-500 hover:bg-yellow-600 text-black">
              <Plus className="w-4 h-4 mr-2" />
              Add Contact
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {contacts.length > 0 ? (
            <div className="space-y-4">
              {contacts.map((contact) => (
                <div key={contact.id} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-gray-900">{contact.name}</h4>
                    {contact.is_primary && (
                      <Badge className="bg-yellow-500 text-black">Primary</Badge>
                    )}
                  </div>
                  <p className="text-sm text-gray-600">{contact.relationship}</p>
                  <div className="mt-2 space-y-1">
                    <div className="flex items-center text-sm text-gray-700">
                      <Phone className="w-4 h-4 mr-2" />
                      {contact.phone_primary}
                    </div>
                    {contact.email && (
                      <div className="flex items-center text-sm text-gray-700">
                        <Mail className="w-4 h-4 mr-2" />
                        {contact.email}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <User className="w-8 h-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-500">No emergency contacts added</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Timeline Tab Component
const TimelineTab = ({ employeeId }) => {
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Activity Timeline</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <Clock className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-500">Timeline feature coming soon</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Document Upload Dialog Component
const DocumentUploadDialog = ({ open, onClose, employeeId, onUpload }) => {
  const [file, setFile] = useState(null);
  const [documentType, setDocumentType] = useState('');
  const [description, setDescription] = useState('');
  const [isConfidential, setIsConfidential] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async () => {
    if (!file || !documentType) return;

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('document', file);
      formData.append('document_type', documentType);
      formData.append('description', description);
      formData.append('is_confidential', isConfidential);

      const document = await apiService.makeRequest(`/api/employees/${employeeId}/documents`, {
        method: 'POST',
        body: formData,
        headers: {}
      });

      onUpload(document);
      onClose();
      
      // Reset form
      setFile(null);
      setDocumentType('');
      setDescription('');
      setIsConfidential(false);

      toast({
        title: 'Success',
        description: 'Document uploaded successfully.'
      });
    } catch (error) {
      console.error('Failed to upload document:', error);
      toast({
        title: 'Error',
        description: 'Failed to upload document.',
        variant: 'destructive'
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Upload Document</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Document Type</Label>
            <Select value={documentType} onValueChange={setDocumentType}>
              <SelectTrigger>
                <SelectValue placeholder="Select document type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="contract">Contract</SelectItem>
                <SelectItem value="certification">Certification</SelectItem>
                <SelectItem value="resume">Resume</SelectItem>
                <SelectItem value="performance_review">Performance Review</SelectItem>
                <SelectItem value="disciplinary">Disciplinary</SelectItem>
                <SelectItem value="training">Training</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>File</Label>
            <Input
              type="file"
              onChange={(e) => setFile(e.target.files[0])}
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            />
          </div>

          <div>
            <Label>Description (Optional)</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of the document..."
              rows={3}
            />
          </div>

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="confidential"
              checked={isConfidential}
              onChange={(e) => setIsConfidential(e.target.checked)}
            />
            <Label htmlFor="confidential">Mark as confidential</Label>
          </div>

          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              onClick={handleUpload}
              disabled={!file || !documentType || uploading}
              className="bg-yellow-500 hover:bg-yellow-600 text-black"
            >
              {uploading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black mr-2"></div>
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EmployeeProfile;